import React from 'react';

import _ from 'lodash';
import { toast } from 'react-toastify';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';

import { OhsFilterList } from 'dashboard/topFilter/OhsFilterModels';
import { OhsQrCodeViewPresets } from 'qr-code/models/OhsQrCodeModels';
import { OhsUser } from 'user/OhsUser';
import { getModuleName, getWorkplaceOptions } from 'user/OhsUserServices';
import { initialGlobalSearchPayload } from 'search/store/OhsSearchSlice';
import { OhsGlobalSearchPayload } from 'search/OhsSearchModels';
import { ConfigByModule, ModuleType } from 'global-services/constants/OhsObject';
import OhsLoginUtils from 'login/OhsLoginUtils';
import { getOhsLocalStorage } from 'global-services/OhsDataParse';
import OhsQrCodeRecord from 'qr-code/models/OhsQrCodeRecord';
import { QrCodeDataLocalStorageName } from 'user/OhsUserModels';
import { OhsMaterialIconButton } from 'global-components/buttons/OhsMaterialButton';
import { getOrgControlDetails } from 'admin2/OhsAdmin2Services';
import OhsConfigurations from 'user/configurations/OhsConfigurations';
import OhsModulePermissions from 'user/configurations/OhsModulePermissions';
import { allocateQrCode } from 'qr-code/OhsQrCodeServices';
import { OhsAllocationItem } from 'global-components/allocation/OhsAllocatableModels';

export const getQrCodeListViewPreset = (tierNum: number): OhsQrCodeViewPresets => {
  switch (tierNum) {
    case 3:
      return OhsQrCodeViewPresets.View1QrCodeList;
    case 4:
      return OhsQrCodeViewPresets.View2QrCodeList;
    default:
      return OhsQrCodeViewPresets.View1QrCodeList;
  }
};

export const getQrCodeFilter = (filter: OhsFilterList, currentViewPreset: OhsQrCodeViewPresets) => {
  const qrCodeFilter = {
    [currentViewPreset]: filter?.qrCodeModule[currentViewPreset],
    sort: filter?.qrCodeModule?.sort,
  };
  return qrCodeFilter;
};

export function renderCreatedBy(row: any): React.ReactNode {
  const createdByName = row.original?.createdBy?.name || '';
  const createdByEmail = row.original?.createdBy?.email || '';
  // eslint-disable-next-line no-nested-ternary
  const displayText = createdByName
    ? createdByEmail
      ? `${createdByName} (${createdByEmail})`
      : createdByName
    : createdByEmail;
  if (displayText) {
    return <div>{displayText}</div>;
  }
  return null;
}

export function renderQrCodeIcon(rowOriginal: any): React.ReactNode {
  const rowQrCodeToken = rowOriginal?.linkaccessToken || '';

  const handleClick = async () => {
    if (rowQrCodeToken && rowQrCodeToken?.length > 0) {
      const rowQrCodeUrl = `${window.location.protocol}//${window.location.host}
      /qr-code/operation/${rowQrCodeToken}`;
      navigator.clipboard.writeText(rowQrCodeUrl);
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      {rowQrCodeToken && (
        <OhsMaterialIconButton onClick={handleClick} title="Click to copy QR Code URL">
          <QrCodeScannerIcon fontSize="large" />
        </OhsMaterialIconButton>
      )}
    </div>
  );
}

export const getQrCodeSearchDefault = (user: OhsUser) => {
  const getWorkplaceLists = getWorkplaceOptions(user?.configs);
  const qrCodeWorkplaceOptions: string[] = getWorkplaceLists.map((list: any) => list.id);
  const loadPresetSearch: OhsGlobalSearchPayload = { ...initialGlobalSearchPayload };

  loadPresetSearch.filter = {
    ...loadPresetSearch.filter,
    workplaces: qrCodeWorkplaceOptions,
    modules: [ModuleType.QrCode],
  };

  return loadPresetSearch;
};

export const getQrCodeAllowedModuleTypeOptions = async (user: OhsUser | undefined) => {
  const returnType: Array<{ value: string; name: string; events: string[] }> = [];

  const moduleOptions = user?.configs?.qrcode?.MODULE_OPTIONS;

  if (!Array.isArray(moduleOptions) || !user) return returnType;

  const adminConfig: any = await getOrgControlDetails(user?.tier?._id);
  if (!Array.isArray(adminConfig?.qrcode)) return returnType;

  const adminQrCodeModules = adminConfig?.qrcode ?? [];

  moduleOptions?.forEach((moduleOption) => {
    const { type: moduleType, enabled, events } = moduleOption;
    if (!enabled || !moduleType || !Array.isArray(events) || events.length < 1) return;

    const adminMod = adminQrCodeModules.find((mod: any) => mod.type === moduleType);

    if (!adminMod || !adminMod.enabled || !Array.isArray(adminMod.events)) return;

    const moduleConfigName = _.get(ConfigByModule, moduleType);
    const userModuleConfig = _.get(user.configs, moduleConfigName);
    const hasCreatePermission = _.get(userModuleConfig, 'PERMISSIONS.create', false);

    const allowedEvents = events
      ?.filter((evt) => evt.enabled)
      ?.map((evt) => {
        const eventName = evt.event;
        const adminEvent = adminMod.events?.find((ae: any) => ae.event === eventName);

        if (!adminEvent?.enabled) return null;
        if (eventName === 'create' && !hasCreatePermission) return null;

        return eventName;
      })
      ?.filter(Boolean) as string[];

    if (allowedEvents.length === 0) return;

    returnType.push({
      value: moduleType,
      name: getModuleName(user?.configs, moduleType),
      events: allowedEvents,
    });
  });

  return returnType;
};

export const decodeQrCodeGuestToken = (
  guestUserToken: string
): { isValidToken: boolean; qrCodeId?: string } => {
  if (guestUserToken && guestUserToken.length > 0) {
    const decoded = OhsLoginUtils.decodeJWT(guestUserToken);

    const exp = decoded?.payload?.exp;
    const type = decoded?.payload?.type;
    const qrCodeId = decoded?.payload?._id;

    if (exp && typeof exp === 'number') {
      const expiryDate = new Date(exp * 1000);

      if (expiryDate > new Date()) {
        return {
          isValidToken: true,
          qrCodeId: type === ModuleType.QrCode ? qrCodeId : undefined,
        };
      }
    }
  }

  return { isValidToken: false, qrCodeId: undefined };
};

export const getLocalOhsGuestQrCodeData = (): OhsQrCodeRecord | undefined => {
  const qrCodeData = getOhsLocalStorage(QrCodeDataLocalStorageName);
  if (qrCodeData && qrCodeData?.type === ModuleType.QrCode) {
    const guestQrCodeData = qrCodeData || new OhsQrCodeRecord();

    if (guestQrCodeData._id) {
      return guestQrCodeData;
    }
  }

  return undefined;
};

export const getQrCodeModuleEventPermission = (
  configs: OhsConfigurations | undefined,
  moduleType: ModuleType | undefined,
  eventType: keyof OhsModulePermissions
): boolean => {
  if (!configs || !moduleType || !eventType) return false;

  switch (moduleType) {
    case ModuleType.Action:
      return configs.action?.PERMISSIONS?.[eventType] ?? false;

    case ModuleType.Incident:
      return configs.incident?.PERMISSIONS?.[eventType] ?? false;

    default:
      return false;
  }
};

export const handleAllocateQrCodeUtil = async (
  id: string,
  allocationItem: OhsAllocationItem | OhsAllocationItem[],
  moduleTypeName: string,
  eventTypeName: string
): Promise<any> => {
  const res = await allocateQrCode(id, allocationItem, {
    disableErrorToast: true,
  });
  if (res?.success === false && res?.error) {
    const errorCodes = res.error.code || [];

    if (errorCodes?.includes('core.module.qrcode:limit_reached')) {
      toast.error(`The maximum number of ${moduleTypeName} ${eventTypeName} qr codes are used.`);
    } else {
      toast.error('QR Code allocation failed.');
    }
  }

  return res;
};
